// components/minimal/MinimalNavMenu.tsx
//
// Flat-dark navigation shell for the minimal theme. Same data
// sources as the classic NavMenu (re-uses useNavPermissions /
// useNavSubscriptions / useTournamentRole) so role gating stays
// consistent across both themes — only the visual layer differs.
//
// Shape:
//   - A 64px top bar with brand wordmark on the left, current
//     tournament chip in the centre (when relevant), and the
//     hamburger / user / break-state cluster on the right.
//   - A slide-in drawer (Framer Motion AnimatePresence) for the
//     full nav, opened from the hamburger. Closed by default on
//     every page so the chrome is invisible until summoned.
//   - Nav items: dense, single-line, accent border on the active
//     route. Cascade-in via MotionStagger.
//
// Items rendered are a focused subset of the classic nav — the
// most-used surfaces for managers / staff. Rare items (deck
// builder helpers, admin migrations, etc.) stay in the classic
// nav. If/when the minimal nav goes from preview to default, we
// can backfill those into a 'More' section.

import { ReactElement, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Avatar, Drawer } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventIcon from '@mui/icons-material/Event';
import GavelIcon from '@mui/icons-material/Gavel';
import VideocamIcon from '@mui/icons-material/Videocam';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { useAuth, UserData } from '../../context/AuthContext';
import { ROUTES } from '../../config/routes';
import {
  useNavPermissions,
  useNavSubscriptions,
  useNavActions,
} from '../layout/NavMenu/hooks';
import { MotionStagger, MotionStaggerItem } from './MotionPage';

const BAR_HEIGHT = 64;
const DRAWER_WIDTH = 280;

const TOP_BAR_BG = 'rgba(14, 17, 24, 0.92)';
const DRAWER_BG = '#0E1118';
const ITEM_HOVER = 'rgba(255, 255, 255, 0.04)';
const ACTIVE_BG = 'rgba(0, 255, 212, 0.08)';
const ACTIVE_BORDER = '#00FFD4';
const ICON_DEFAULT = '#8892A0';
const ICON_ACTIVE = '#00FFD4';

interface NavItem {
  path: string;
  label: string;
  Icon: typeof DashboardIcon;
  show: boolean;
}

export interface MinimalNavMenuProps {
  user: UserData | null;
}

export function MinimalNavMenu({ user }: MinimalNavMenuProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const [open, setOpen] = useState(false);

  // Re-use the classic nav's permission + subscriptions hooks so
  // role gating + on-break state match one-to-one. We don't show
  // appeals / penalties badges in the minimal nav, so we skip
  // useNavBadgeCounts entirely — the always-on subscriptions hook
  // is permission-free and only mounts once.
  const subscriptions = useNavSubscriptions({ user });
  const permissions = useNavPermissions({
    user,
    isActingLeadFromTeams: subscriptions.isActingLeadFromTeams,
    tournamentRole: null,
  });
  const { toggleBreak } = useNavActions({
    user,
    onBreak: subscriptions.onBreak,
  });
  const onBreak = subscriptions.onBreak;

  const doLogout = async () => {
    try {
      await logout();
    } catch {
      // logout failures are surfaced via the auth context's own
      // error state; here we just need to fall through so the
      // drawer can close.
    }
  };

  const items: NavItem[] = [
    {
      path: ROUTES.LANDING,
      label: 'Dashboard',
      Icon: DashboardIcon,
      show: true,
    },
    {
      path: ROUTES.TOURNAMENT_SERIES,
      label: 'Tournament Series',
      Icon: EventIcon,
      show: permissions.canManage,
    },
    {
      path: ROUTES.DQ_CASES,
      label: 'DQ Cases',
      Icon: GavelIcon,
      show: true,
    },
    {
      path: ROUTES.STREAM_MANAGER,
      label: 'Stream',
      Icon: VideocamIcon,
      show: permissions.canViewStream,
    },
    {
      path: ROUTES.STREAMING_TOP_CUT,
      label: 'Top Cut Builder',
      Icon: EmojiEventsIcon,
      show: permissions.canViewStream,
    },
  ];

  const visibleItems = items.filter((i) => i.show);
  const currentPath = location.pathname;
  const isActive = (path: string) =>
    currentPath === path || currentPath.startsWith(path + '/');

  const close = () => setOpen(false);

  const handleNav = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Top bar — sticky, light blur, single accent on the brand */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BAR_HEIGHT,
          backgroundColor: TOP_BAR_BG,
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          gap: 2,
        }}
      >
        <IconButton
          onClick={() => setOpen(true)}
          aria-label="Open navigation"
          sx={{ color: '#FFFFFF' }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          onClick={() => navigate(ROUTES.LANDING)}
          sx={{
            color: '#FFFFFF',
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            letterSpacing: '0.02em',
            cursor: 'pointer',
            // Single accent on the brand — same cyan we use for
            // active state in the drawer. Keeps the brand from
            // being purely white-on-black.
            '& span': { color: '#00FFD4' },
          }}
        >
          Round<span>Master</span>
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Break-state pill — clickable, animates the icon swap
            on toggle so the operator gets immediate feedback. */}
        <motion.div
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 24 }}
        >
          <IconButton
            onClick={toggleBreak}
            aria-label={onBreak ? 'End break' : 'Start break'}
            sx={{
              color: onBreak ? '#FF3366' : '#00FF88',
              border: '1px solid',
              borderColor: onBreak
                ? 'rgba(255, 51, 102, 0.4)'
                : 'rgba(0, 255, 136, 0.4)',
              borderRadius: '8px',
              p: '6px',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={onBreak ? 'break' : 'active'}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'flex' }}
              >
                {onBreak ? <RestaurantIcon /> : <CheckCircleOutlineIcon />}
              </motion.div>
            </AnimatePresence>
          </IconButton>
        </motion.div>

        <Avatar
          onClick={() => navigate('/user-settings')}
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'rgba(0, 255, 212, 0.12)',
            color: '#00FFD4',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            border: '1px solid rgba(0, 255, 212, 0.25)',
          }}
        >
          {(user?.firstName?.[0] || user?.email?.[0] || '?').toUpperCase()}
        </Avatar>
      </Box>

      {/* Drawer — Framer-motion animated overlay. Uses MUI Drawer
          for the focus trap + backdrop behaviour, custom paper
          styles for the flat look. */}
      <Drawer
        anchor="left"
        open={open}
        onClose={close}
        ModalProps={{ keepMounted: false }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            backgroundColor: DRAWER_BG,
            color: '#FFFFFF',
            borderRight: '1px solid rgba(255, 255, 255, 0.04)',
            backgroundImage: 'none',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(8, 10, 16, 0.6)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
            }}
          >
            <Typography
              sx={{
                color: '#FFFFFF',
                fontFamily: '"Rajdhani", sans-serif',
                fontWeight: 700,
                fontSize: '1.05rem',
              }}
            >
              Navigation
            </Typography>
            <IconButton size="small" onClick={close} sx={{ color: '#8892A0' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Item list — staggered entry. The MotionStagger only
              fires on the FIRST open per page; reopens don't
              re-animate (mode=keepMounted is off so the drawer
              tears down on close). */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
            <MotionStagger>
              {visibleItems.map(({ path, label, Icon }) => {
                const active = isActive(path);
                return (
                  <MotionStaggerItem key={path}>
                    <motion.div
                      whileHover={{ x: 2 }}
                      whileTap={{ scale: 0.99 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                    >
                      <Box
                        onClick={() => handleNav(path)}
                        sx={{
                          mx: 1,
                          my: 0.25,
                          px: 2,
                          py: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          position: 'relative',
                          backgroundColor: active ? ACTIVE_BG : 'transparent',
                          transition: 'background-color 150ms ease',
                          '&:hover': {
                            backgroundColor: active ? ACTIVE_BG : ITEM_HOVER,
                          },
                          // 2px accent strip on the left for
                          // active items — same role as the
                          // accent prop on MinimalCard.
                          ...(active && {
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 6,
                              bottom: 6,
                              width: '2px',
                              backgroundColor: ACTIVE_BORDER,
                              borderRadius: '0 2px 2px 0',
                            },
                          }),
                        }}
                      >
                        <Icon
                          sx={{
                            color: active ? ICON_ACTIVE : ICON_DEFAULT,
                            fontSize: '1.2rem',
                          }}
                        />
                        <Typography
                          sx={{
                            color: active ? '#FFFFFF' : '#C9D1D9',
                            fontSize: '0.95rem',
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    </motion.div>
                  </MotionStaggerItem>
                );
              })}
            </MotionStagger>
          </Box>

          {/* Footer — Settings / Switch to player / Logout. Not
              part of the stagger so they feel grouped + stable. */}
          <Box
            sx={{
              borderTop: '1px solid rgba(255, 255, 255, 0.04)',
              py: 1,
            }}
          >
            <FooterItem
              label="Settings"
              Icon={SettingsIcon}
              color="#8892A0"
              onClick={() => handleNav('/user-settings')}
              active={isActive('/user-settings')}
            />
            <FooterItem
              label="Player dashboard"
              Icon={SwapHorizIcon}
              color="#7B68EE"
              onClick={() => handleNav('/player')}
            />
            <FooterItem
              label="Log out"
              Icon={LogoutIcon}
              color="#FF3366"
              onClick={doLogout}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

interface FooterItemProps {
  label: string;
  Icon: typeof DashboardIcon;
  color: string;
  onClick: () => void;
  active?: boolean;
}

function FooterItem({
  label,
  Icon,
  color,
  onClick,
  active,
}: FooterItemProps): ReactElement {
  return (
    <motion.div
      whileHover={{ x: 2 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: 'spring', stiffness: 500, damping: 32 }}
    >
      <Box
        onClick={onClick}
        sx={{
          mx: 1,
          my: 0.25,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: '8px',
          cursor: 'pointer',
          backgroundColor: active ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
        }}
      >
        <Icon sx={{ color, fontSize: '1.1rem' }} />
        <Typography sx={{ color, fontSize: '0.9rem', fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </motion.div>
  );
}

export default MinimalNavMenu;
